import React, { useState, useMemo } from 'react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { 
  Calendar, 
  Filter, 
  TrendingUp, 
  ShoppingBag, 
  Clock, 
  CheckCircle2, 
  XCircle, 
  ChevronRight, 
  Printer, 
  FileText,
  DollarSign,
  Briefcase
} from 'lucide-react';
import { format, parseISO, isWithinInterval, startOfDay, endOfDay } from 'date-fns';

interface ProcurementHistoryReportProps {
  lpos: any[];
  requisitions: any[];
}

const COLORS = ['#1e3a8a', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

export const ProcurementHistoryReport: React.FC<ProcurementHistoryReportProps> = ({ lpos, requisitions }) => {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedDept, setSelectedDept] = useState('all');

  // Parse timestamps
  const parsedLPOs = useMemo(() => {
    return lpos.map(lpo => {
      const date = lpo.createdAt?.toDate ? lpo.createdAt.toDate() : new Date();
      return {
        ...lpo,
        jsDate: date,
        formattedDate: date.toLocaleDateString(),
        amount: lpo.totalAmount || 0
      };
    });
  }, [lpos]);

  const parsedRequisitions = useMemo(() => {
    return requisitions.map(req => {
      const date = req.date?.toDate ? req.date.toDate() : (req.createdAt?.toDate ? req.createdAt.toDate() : new Date());
      return {
        ...req,
        jsDate: date,
        formattedDate: date.toLocaleDateString(),
        cost: (req.estimatedCost || 0) * (req.quantity || 1)
      };
    });
  }, [requisitions]);

  // Filter based on date range & department
  const filteredLpos = useMemo(() => {
    return parsedLPOs.filter(lpo => {
      if (dateFrom && dateTo) {
        const from = startOfDay(parseISO(dateFrom));
        const to = endOfDay(parseISO(dateTo));
        return isWithinInterval(lpo.jsDate, { start: from, end: to });
      }
      return true;
    });
  }, [parsedLPOs, dateFrom, dateTo]);

  const filteredRequisitions = useMemo(() => {
    return parsedRequisitions.filter(req => {
      // Date filter
      if (dateFrom && dateTo) {
        const from = startOfDay(parseISO(dateFrom));
        const to = endOfDay(parseISO(dateTo));
        if (!isWithinInterval(req.jsDate, { start: from, end: to })) {
          return false;
        }
      }
      // Department filter
      if (selectedDept !== 'all') {
        const reqDept = req.department || 'TECHNICAL';
        return reqDept.toLowerCase() === selectedDept.toLowerCase();
      }
      return true;
    });
  }, [parsedRequisitions, dateFrom, dateTo, selectedDept]);

  // Get departments list
  const departments = useMemo(() => {
    const set = new Set<string>();
    parsedRequisitions.forEach(r => {
      if (r.department) set.add(r.department.toUpperCase());
    });
    return Array.from(set);
  }, [parsedRequisitions]);

  // Budget calculations
  const totalLpoValue = useMemo(() => {
    return filteredLpos.reduce((sum, item) => sum + item.amount, 0);
  }, [filteredLpos]);

  const totalReqValue = useMemo(() => {
    return filteredRequisitions.reduce((sum, item) => sum + item.cost, 0);
  }, [filteredRequisitions]);

  // Budget utilization per department
  const budgetUtilizationData = useMemo(() => {
    const utilization: Record<string, { department: string; estimated: number; approved: number }> = {};
    
    // Group requisitions by department
    filteredRequisitions.forEach(req => {
      const dept = (req.department || 'TECHNICAL').toUpperCase();
      if (!utilization[dept]) {
        utilization[dept] = { department: dept, estimated: 0, approved: 0 };
      }
      utilization[dept].estimated += req.cost;
    });

    // Match LPOs if possible or distribute by department budget
    // Assuming LPOs linked to requisitions or showing requisitions that got 'lpo_created'
    return Object.values(utilization);
  }, [filteredRequisitions]);

  // Pending deliveries: LPOs approved/sent but not marked as delivered
  const pendingDeliveries = useMemo(() => {
    return filteredLpos.filter(lpo => lpo.status !== 'approved' || lpo.deliveryTerms?.toLowerCase().includes('immediate') || !lpo.isDelivered);
  }, [filteredLpos]);

  const approvedLpos = useMemo(() => {
    return filteredLpos.filter(lpo => lpo.status === 'approved');
  }, [filteredLpos]);

  const approvalPendingLpos = useMemo(() => {
    return filteredLpos.filter(lpo => lpo.status === 'pending_hod' || lpo.status === 'pending_finance');
  }, [filteredLpos]);

  return (
    <div className="space-y-8 animate-fadeIn" id="procurement-history-report-section">
      {/* Date filter bar */}
      <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm flex flex-col md:flex-row items-end gap-4">
        <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-4 w-full">
          <div className="space-y-1.5">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" />
              <span>Date From</span>
            </label>
            <input 
              type="date" 
              value={dateFrom} 
              onChange={e => setDateFrom(e.target.value)}
              className="w-full px-4 py-2.5 bg-slate-50 border border-slate-100 rounded-2xl font-bold text-sm text-slate-700" 
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" />
              <span>Date To</span>
            </label>
            <input 
              type="date" 
              value={dateTo} 
              onChange={e => setDateTo(e.target.value)}
              className="w-full px-4 py-2.5 bg-slate-50 border border-slate-100 rounded-2xl font-bold text-sm text-slate-700" 
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1">
              <Filter className="w-3.5 h-3.5" />
              <span>Department</span>
            </label>
            <select 
              value={selectedDept} 
              onChange={e => setSelectedDept(e.target.value)}
              className="w-full px-4 py-2.5 bg-slate-50 border border-slate-100 rounded-2xl font-bold text-sm text-slate-700"
            >
              <option value="all">All Departments</option>
              {departments.map(d => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>
        </div>
        {(dateFrom || dateTo || selectedDept !== 'all') && (
          <button 
            onClick={() => { setDateFrom(''); setDateTo(''); setSelectedDept('all'); }}
            className="px-5 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-2xl font-bold text-sm transition-all"
          >
            Clear
          </button>
        )}
      </div>

      {/* KPI Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-900 flex items-center justify-center">
            <ShoppingBag className="w-6 h-6" />
          </div>
          <div>
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Total LPO Value</p>
            <h3 className="text-xl font-black text-slate-900 mt-0.5">TZS {totalLpoValue.toLocaleString()}</h3>
            <span className="text-[10px] text-slate-400 font-bold block">{filteredLpos.length} Generated Orders</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
            <CheckCircle2 className="w-6 h-6" />
          </div>
          <div>
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Approved Budget</p>
            <h3 className="text-xl font-black text-slate-900 mt-0.5">TZS {approvedLpos.reduce((sum, item) => sum + item.amount, 0).toLocaleString()}</h3>
            <span className="text-[10px] text-emerald-600 font-bold block">{approvedLpos.length} Fully Active LPOs</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center">
            <Clock className="w-6 h-6" />
          </div>
          <div>
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Approval Pending</p>
            <h3 className="text-xl font-black text-slate-900 mt-0.5">TZS {approvalPendingLpos.reduce((sum, item) => sum + (item.amount || 0), 0).toLocaleString()}</h3>
            <span className="text-[10px] text-amber-500 font-bold block">{approvalPendingLpos.length} Awaiting Signatures</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-rose-50 text-rose-500 flex items-center justify-center">
            <TrendingUp className="w-6 h-6" />
          </div>
          <div>
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Total Requisitions</p>
            <h3 className="text-xl font-black text-slate-900 mt-0.5">TZS {(totalReqValue || 0).toLocaleString()}</h3>
            <span className="text-[10px] text-rose-500 font-bold block">{filteredRequisitions.length} Requests Raised</span>
          </div>
        </div>
      </div>

      {/* Main analytics grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Department Budget Utilization Chart */}
        <div className="bg-white p-6 rounded-[2.5rem] border border-slate-100 shadow-sm">
          <h4 className="text-base font-black text-slate-900 mb-6 flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-blue-900" />
            <span>Budget Utilization per Department (TZS)</span>
          </h4>
          {budgetUtilizationData.length === 0 ? (
            <div className="h-64 flex flex-col items-center justify-center text-slate-400 text-sm">
              No department requisition data found for this range.
            </div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={budgetUtilizationData} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="department" stroke="#94a3b8" fontSize={11} tickLine={false} />
                  <YAxis stroke="#94a3b8" fontSize={11} tickLine={false} />
                  <Tooltip formatter={(value: any) => `TZS ${(value || 0).toLocaleString()}`} />
                  <Bar dataKey="estimated" name="Estimated cost" fill="#3b82f6" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="approved" name="Approved spent" fill="#1e3a8a" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Pending Deliveries and Tracking */}
        <div className="bg-white p-6 rounded-[2.5rem] border border-slate-100 shadow-sm flex flex-col justify-between">
          <div>
            <h4 className="text-base font-black text-slate-900 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-orange-500" />
              <span>Pending Deliveries Tracking</span>
            </h4>
            <p className="text-xs font-medium text-slate-500 mb-4">Purchase orders fully authorized or pending immediate delivery warehouse ingestion.</p>
            
            <div className="space-y-3.5 max-h-[17rem] overflow-y-auto pr-1">
              {pendingDeliveries.length === 0 ? (
                <div className="py-12 text-center text-slate-400 text-xs italic">
                  All active deliveries fulfilled.
                </div>
              ) : (
                pendingDeliveries.map((lpo, idx) => (
                  <div key={lpo.id || idx} className="flex items-center justify-between p-3.5 bg-slate-50 border border-slate-100 rounded-2xl">
                    <div>
                      <span className="text-[10px] font-black text-blue-900 bg-blue-50 px-2 py-0.5 rounded-md">{lpo.lpoNumber}</span>
                      <h5 className="text-xs font-bold text-slate-800 mt-1.5">{lpo.supplierName}</h5>
                      <p className="text-[9px] text-slate-400 font-medium mt-0.5">Delivery terms: {lpo.deliveryTerms}</p>
                    </div>
                    <div className="text-right">
                      <span className="text-xs font-black text-slate-900 block">{lpo.currency} {(lpo.totalAmount || 0).toLocaleString()}</span>
                      <span className={`inline-block px-2 py-0.5 text-[9px] font-bold rounded-full mt-1.5 uppercase ${
                        lpo.status === 'approved' ? 'bg-orange-50 text-orange-600 border border-orange-100' : 'bg-slate-100 text-slate-500'
                      }`}>
                        {lpo.status === 'approved' ? 'Ready for Dispatch' : 'Awaiting Approval'}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Detailed PO list inside date range */}
      <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm p-6">
        <h4 className="text-base font-black text-slate-900 mb-6 flex items-center gap-2">
          <FileText className="w-5 h-5 text-blue-900" />
          <span>Purchase Order & Budget utilization Log</span>
        </h4>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100 text-slate-500 font-black uppercase">
                <th className="p-4">PO Reference</th>
                <th className="p-4">Date</th>
                <th className="p-4">Supplier name</th>
                <th className="p-4">Delivery Terms</th>
                <th className="p-4">Department / Purpose</th>
                <th className="p-4">Status</th>
                <th className="p-4 text-right">Order Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 font-bold text-slate-700">
              {filteredLpos.length === 0 ? (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-slate-400 italic">No purchase orders found matching filters.</td>
                </tr>
              ) : (
                filteredLpos.map((lpo, idx) => (
                  <tr key={lpo.id || idx} className="hover:bg-slate-50/50 transition-colors">
                    <td className="p-4 font-black text-blue-900">{lpo.lpoNumber}</td>
                    <td className="p-4 text-slate-400 font-medium">{lpo.formattedDate}</td>
                    <td className="p-4 text-slate-900 font-black">{lpo.supplierName}</td>
                    <td className="p-4 text-slate-500 font-medium">{lpo.deliveryTerms}</td>
                    <td className="p-4 text-slate-500 font-medium">
                      {lpo.items?.[0]?.description || 'General Supplies'}
                    </td>
                    <td className="p-4">
                      <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase border ${
                        lpo.status === 'approved' ? 'bg-green-50 text-green-700 border-green-100' :
                        lpo.status === 'pending_hod' ? 'bg-amber-50 text-amber-700 border-amber-100' :
                        lpo.status === 'pending_finance' ? 'bg-blue-50 text-blue-700 border-blue-100' :
                        'bg-red-50 text-red-700 border-red-100'
                      }`}>
                        {lpo.status === 'pending_hod' ? 'Pending HOD' : 
                         lpo.status === 'pending_finance' ? 'Pending Finance' : 
                         lpo.status.toUpperCase()}
                      </span>
                    </td>
                    <td className="p-4 text-right font-black text-slate-900">{lpo.currency} {(lpo.totalAmount || 0).toLocaleString()}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
